import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RefreshIcon from "@mui/icons-material/Refresh";

import { djangoApi } from "../../utils/axios/axios.utils";

function fmtDateBR(iso) {
    const s = String(iso || "").slice(0, 10);
    if (!s) return "-";
    const [y, m, d] = s.split("-");
    if (!y || !m || !d) return s;
    return `${d}/${m}/${y}`;
}

function fmtDateTimeBR(iso) {
    if (!iso) return "-";
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return String(iso);

    return dt.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export default function SellerGeneratedBillNotesAccordion({
    seller,
    theme,
    colors,
    subtleBorder,
    softText,
}) {
    const sellerId = seller?.id;

    const [expanded, setExpanded] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadedOnce, setLoadedOnce] = useState(false);
    const [error, setError] = useState("");

    const headers = useMemo(
        () => ({
            Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
            "Content-Type": "application/json",
        }),
        []
    );

    const fetchNotes = useCallback(async () => {
        if (!sellerId) {
            setRows([]);
            setLoadedOnce(false);
            setError("");
            return;
        }

        try {
            setLoading(true);
            setError("");

            const res = await djangoApi.get(
                "products/kits_active/get_generated_bill_notes_by_seller/",
                {
                    params: { seller_id: sellerId },
                    headers,
                }
            );

            const list = Array.isArray(res?.data?.data) ? res.data.data : [];
            setRows(list);
            setLoadedOnce(true);
        } catch (err) {
            console.log("Erro ao listar observações de cobrança", err);
            setRows([]);
            setLoadedOnce(true);
            setError("Falha ao carregar observações de cobrança. Veja o console.");
        } finally {
            setLoading(false);
        }
    }, [sellerId, headers]);

    const handleToggleAccordion = async (_, isExpanded) => {
        setExpanded(isExpanded);

        if (isExpanded && !loadedOnce) {
            await fetchNotes();
        }
    };

    useEffect(() => {
        setRows([]);
        setLoadedOnce(false);
        setError("");

        if (sellerId) {
            fetchNotes();
        }
    }, [sellerId]); // eslint-disable-line react-hooks/exhaustive-deps

    const totalNotes = useMemo(() => {
        return rows.reduce((acc, kit) => acc + Number(kit?.notes_count || 0), 0);
    }, [rows]);

    const headerRight = (
        <Stack direction="row" spacing={1} alignItems="center">
            {loading && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="body2" color={softText}>
                        Carregando…
                    </Typography>
                </Stack>
            )}

            <Chip
                size="small"
                variant="outlined"
                label={
                    sellerId
                        ? `${rows.length} kit${rows.length === 1 ? "" : "s"} · ${totalNotes} observaç${totalNotes === 1 ? "ão" : "ões"}`
                        : "sem vendedora"
                }
            />

            <Tooltip title="Recarregar">
                <span>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            fetchNotes();
                        }}
                        disabled={!sellerId || loading}
                    >
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        </Stack>
    );

    return (
        <Paper
            elevation={8}
            sx={{
                backgroundColor: colors?.pink?.[900],
                borderRadius: 2,
                border: subtleBorder,
            }}
        >
            <Accordion
                expanded={expanded}
                onChange={handleToggleAccordion}
                disableGutters
                sx={{
                    bgcolor: "transparent",
                    color: "inherit",
                    boxShadow: "none",
                    "&:before": { display: "none" },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: "100%" }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <ReceiptLongIcon fontSize="small" />
                            <Typography fontWeight={900}>Observações de Fatura</Typography>
                            {!!seller?.nome && (
                                <Chip size="small" variant="outlined" label={seller.nome} />
                            )}
                        </Stack>
                        {headerRight}
                    </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 2, pb: 2 }}>
                    <Divider sx={{ mb: 2 }} />

                    {!sellerId ? (
                        <Typography variant="body2" color={softText}>
                            Selecione uma vendedora para carregar as observações.
                        </Typography>
                    ) : error ? (
                        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                            {error}
                        </Typography>
                    ) : !loading && rows.length === 0 ? (
                        <Typography variant="body2" color={softText}>
                            Nenhuma observação de fatura encontrada para esta vendedora.
                        </Typography>
                    ) : (
                        <Stack spacing={2}>
                            {rows.map((kit) => {
                                const notes = Array.isArray(kit?.generated_bill_notes)
                                    ? kit.generated_bill_notes
                                    : [];

                                return (
                                    <Paper
                                        key={kit.id}
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 2,
                                            border: subtleBorder,
                                            p: 2,
                                            bgcolor:
                                                theme?.palette?.mode === "dark"
                                                    ? colors?.pink?.[800]
                                                    : "#fff",
                                        }}
                                    >
                                        <Stack spacing={1.25}>
                                            <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                spacing={1}
                                                justifyContent="space-between"
                                                alignItems={{ xs: "flex-start", sm: "center" }}
                                            >
                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={`Kit ${kit.kit_number || "-"}`}
                                                    />
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={`Criação: ${fmtDateBR(kit.created_at)}`}
                                                    />
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={`Acerto: ${fmtDateBR(kit.pay_date)}`}
                                                    />
                                                    <Chip
                                                        size="small"
                                                        color={kit.is_active ? "success" : "default"}
                                                        variant="outlined"
                                                        label={kit.is_active ? "ATIVO" : "FECHADO"}
                                                    />
                                                </Stack>

                                                <Chip
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                    label={`${notes.length} observaç${notes.length === 1 ? "ão" : "ões"}`}
                                                />
                                            </Stack>

                                            <Box mt={0.5}>
                                                {notes.map((item, idx) => {
                                                    const ts = item?.ts || null;
                                                    const note = item?.note || "-";

                                                    return (
                                                        <Box
                                                            key={`${kit.id}-${idx}`}
                                                            sx={{
                                                                py: 1,
                                                                display: 'flex',
                                                                gap: 2,
                                                                borderBottom:
                                                                    idx !== notes.length - 1
                                                                        ? `1px solid rgba(255,255,255,0.08)`
                                                                        : "none",
                                                            }}
                                                        >
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                {fmtDateTimeBR(ts)} 
                                                            </Typography>

                                                            <Typography variant="body2">
                                                                {note}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    )}
                </AccordionDetails>
            </Accordion>
        </Paper>
    );
}